import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  getBrokersTasksByIdTaskIdPlaywrightCommand,
  getGetBrokersTasksByIdTaskIdResultsQueryKey,
  getGetBrokersTasksTaskIdQueryKey,
  getGetBrokersTaskWorkflowsIdQueryKey,
  useDeleteBrokersTasksByIdTaskIdLease,
  useGetBrokersTasksByIdTaskIdResults,
  usePatchBrokersTasks,
  usePostBrokersTasksByIdTaskIdExecute,
  usePostBrokersTasksByIdTaskIdResetToPending,
} from 'utils/web_api'

const POLLING_INTERVAL_MS = 3000

export const useTaskAdminActions = (
  taskId: string | null | undefined,
  workflowId: string | null | undefined,
  enabled: boolean = true
) => {
  const queryClient = useQueryClient()
  const taskIdOrFallback = taskId ?? ''

  const [executionHistoryVersion, setExecutionHistoryVersion] = useState(0)
  const [includeSafety, setIncludeSafety] = useState(true)
  const [ignoreHold, setIgnoreHold] = useState(false)

  const taskResultsQuery = useGetBrokersTasksByIdTaskIdResults(taskIdOrFallback, {
    query: {
      enabled: enabled && Boolean(taskId),
      refetchInterval: (data) => {
        if (!enabled || !taskId) return false
        const isProcessing = data.state.data?.some((result) => result.status === 'processing')
        return isProcessing ? POLLING_INTERVAL_MS : false
      },
      refetchIntervalInBackground: false,
    },
  })
  const taskResults = taskResultsQuery.data ?? []

  const executeTaskMutation = usePostBrokersTasksByIdTaskIdExecute()
  const resetTaskMutation = usePostBrokersTasksByIdTaskIdResetToPending()
  const clearLeaseMutation = useDeleteBrokersTasksByIdTaskIdLease()
  const buildPlaywrightCommandMutation = useMutation({
    mutationKey: ['build-playwright-command', taskId],
    mutationFn: ({ taskId, includeSafety, ignoreHold }: { taskId: string; includeSafety: boolean; ignoreHold: boolean }) =>
      getBrokersTasksByIdTaskIdPlaywrightCommand(taskId, { safety: includeSafety, ignore_hold: ignoreHold }),
  })
  const bulkUpdateMutation = usePatchBrokersTasks()

  const invalidateTaskQueries = async () => {
    if (!taskId) return

    const promises: Array<Promise<unknown>> = [
      queryClient.invalidateQueries({
        queryKey: getGetBrokersTasksTaskIdQueryKey(taskId),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetBrokersTasksByIdTaskIdResultsQueryKey(taskId),
      }),
    ]

    if (workflowId) {
      promises.push(
        queryClient.invalidateQueries({
          queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
        })
      )
    }

    await Promise.all(promises)
  }

  const handleExecuteTask = () => {
    if (!taskId) return

    executeTaskMutation.mutate(
      { taskId, data: {} },
      {
        onSuccess: async () => {
          toast.success('Task execution queued', { position: 'top-center' })
          await invalidateTaskQueries()
        },
        onError: (error) => {
          const message = (error as { message?: string })?.message || 'Failed to execute task'
          toast.error(message)
        },
      }
    )
  }

  const handleResetToPending = () => {
    if (!taskId) return

    resetTaskMutation.mutate(
      { taskId },
      {
        onSuccess: async () => {
          toast.success('Task reset to pending', { position: 'top-center' })
          await invalidateTaskQueries()
        },
        onError: (error) => {
          const message = (error as { message?: string })?.message || 'Failed to reset task'
          toast.error(message)
        },
      }
    )
  }

  const handleFailTask = () => {
    if (!taskId) return

    bulkUpdateMutation.mutate(
      {
        data: {
          task_ids: [taskId],
          status_event: 'fail',
          status_message: 'Task manually moved to failed state by broker',
        },
      },
      {
        onSuccess: async () => {
          toast.success('Task moved to failed state', { position: 'top-center' })
          await invalidateTaskQueries()
        },
        onError: (error) => {
          const message = (error as { message?: string })?.message || 'Failed to fail task'
          toast.error(message)
        },
      }
    )
  }

  const handleClearLease = (force = false) => {
    if (!taskId) return

    clearLeaseMutation.mutate(
      { taskId, params: force ? { force: true } : undefined },
      {
        onSuccess: async () => {
          toast.success('Task lease cleared', { position: 'top-center' })
          await invalidateTaskQueries()
        },
        onError: (error) => {
          const message = (error as { message?: string })?.message || 'Failed to clear lease'

          if (!force && message.toLowerCase().includes('still active')) {
            const shouldForce = window.confirm('The lease is still active. Force clear it?')
            if (shouldForce) {
              handleClearLease(true)
              return
            }
          }

          toast.error(message)
        },
      }
    )
  }

  const handleCopyPlaywrightCommand = async () => {
    if (!taskId) {
      return
    }

    try {
      const command = await buildPlaywrightCommandMutation.mutateAsync({
        taskId,
        includeSafety,
        ignoreHold,
      })
      await navigator.clipboard.writeText(command.command)
      toast.success('Playwright command copied to clipboard', {
        position: 'top-center',
      })
    } catch (error) {
      console.error('Failed to copy command', error)
      toast.error('Unable to build Playwright command')
    }
  }

  return {
    // State
    executionHistoryVersion,
    setExecutionHistoryVersion,
    includeSafety,
    setIncludeSafety,
    ignoreHold,
    setIgnoreHold,
    // Data
    taskResults,
    taskResultsQuery,
    // Mutations
    executeTaskMutation,
    resetTaskMutation,
    clearLeaseMutation,
    buildPlaywrightCommandMutation,
    bulkUpdateMutation,
    // Handlers
    handleExecuteTask,
    handleResetToPending,
    handleFailTask,
    handleClearLease,
    handleCopyPlaywrightCommand,
  }
}
